import React, { useState, useEffect, useRef } from 'react';
import { SalesAgentWrapper, ChatContainer, MessageContainer, InputContainer } from './SalesAgent.styled';
import MessageBubble from './MessageBubble';

// Define types inline to avoid import issues
interface SalesAgentMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  isError?: boolean;
  metadata?: {
    intent?: any;
    lead_score?: number;
    handoff_needed?: boolean;
    next_steps?: string[];
    contact_options?: ContactOption[];
  };
}

interface SalesAgentConfig {
  organization_name?: string;
  welcomeMessage?: string;
  product_info?: {
    name?: string;
    description?: string;
    features?: string[];
    benefits?: string[];
  };
  pricing_info?: {
    model?: string;
    starting_price?: string;
    tiers?: string[];
  };
  security_info?: {
    certifications?: string[];
    compliance?: string[];
    data_protection?: string;
  };
  contact_options?: {
    email?: string;
    phone?: string;
    calendar?: string;
    chat?: boolean;
  };
  handoff_criteria?: {
    score_threshold?: number;
    max_messages?: number;
  };
  sandbox_restrictions?: string[];
}

interface ContactOption {
  type: 'email' | 'phone' | 'calendar' | 'handoff' | 'chat';
  label: string;
  value: string;
  priority?: number;
}

interface SalesAgentProps {
  organizationId: string;
  config?: SalesAgentConfig;
  onHandoff?: (handoffData: any) => void;
  onQualification?: (qualificationData: any) => void;
  className?: string;
}

const SalesAgent: React.FC<SalesAgentProps> = ({
  organizationId,
  config,
  onHandoff,
  onQualification,
  className
}) => {
  const [messages, setMessages] = useState<SalesAgentMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Debug log to check if component is rendering
  console.log('SalesAgent component rendering:', { organizationId, isOpen, config });

  useEffect(() => {
    try {
      // Initialize session
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
      
      // Add welcome message
      const welcomeMessage = config?.welcomeMessage || "Hi! I'm here to help you learn about our solution. What brings you here today?";
      setMessages([{
        id: 'welcome',
        content: welcomeMessage,
        role: 'assistant',
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error initializing SalesAgent:', error);
      // Still render the component even if initialization fails
      setMessages([{
        id: 'welcome',
        content: "Hi! I'm here to help you learn about our solution. What brings you here today?",
        role: 'assistant',
        timestamp: new Date()
      }]);
    }
  }, [config]);

  const generateSessionId = (): string => {
    return 'session_' + Math.random().toString(36).substr(2, 9);
  };

  const generateMessageId = (): string => {
    return 'msg_' + Math.random().toString(36).substr(2, 9);
  };

  const sendMessage = async (message?: string) => {
    const userMessage = message || inputMessage;
    if (!userMessage.trim()) return;

    setIsLoading(true);
    setInputMessage('');

    // Add user message
    const userMsg: SalesAgentMessage = {
      id: generateMessageId(),
      content: userMessage,
      role: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      // Check if we have a base URL configured
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      
      // Send to backend
      const response = await fetch(`${baseUrl}/sales-agent/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          user_context: {
            organization_id: organizationId,
            page_context: window.location.pathname,
            user_agent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            current_message: userMessage
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Add assistant response
      const assistantMsg: SalesAgentMessage = {
        id: generateMessageId(),
        content: data.response || "I'm here to help! What would you like to know about our solution?",
        role: 'assistant',
        timestamp: new Date(),
        metadata: {
          intent: data.intent,
          lead_score: data.lead_score,
          handoff_needed: data.handoff_needed,
          next_steps: data.next_steps,
          contact_options: data.contact_options
        }
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Handle handoff if needed
      if (data.handoff_needed && onHandoff) {
        onHandoff(data);
      }

      // Handle qualification updates
      if (data.lead_score && onQualification) {
        onQualification(data);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Add a helpful error message that doesn't break the UX
      const errorMsg: SalesAgentMessage = {
        id: generateMessageId(),
        content: "I'm here to help! I can answer questions about our solution, pricing, security, and more. What would you like to know?",
        role: 'assistant',
        timestamp: new Date(),
        isError: false // Don't mark as error to avoid breaking UX
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleContactOption = (option: ContactOption) => {
    if (option.type === 'handoff') {
      // Handle handoff request
      if (onHandoff) {
        onHandoff({
          session_id: sessionId,
          organization_id: organizationId,
          handoff_reason: 'User requested handoff',
          contact_preferences: [option.type]
        });
      }
    } else {
      // Handle other contact options (email, phone, calendar)
      window.open(option.value, '_blank');
    }
  };

  useEffect(() => {
    // Scroll to bottom when new messages are added
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isOpen) {
    return (
      <>
        {/* Debug element - remove this later */}
        <div 
          style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            background: 'red',
            color: 'white',
            padding: '5px',
            fontSize: '12px',
            zIndex: 9999
          }}
        >
          SalesAgent Loaded
        </div>
        <button 
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#007bff',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Chat with Sales Agent"
        >
          💬
        </button>
      </>
    );
  }

  return (
    <SalesAgentWrapper className={className}>
      <ChatContainer>
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid #e5e5e5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0 }}>Sales Assistant</h3>
          <button 
            onClick={() => setIsOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
        
        <MessageContainer>
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              config={config}
              onContactOption={handleContactOption}
            />
          ))}
          {isLoading && (
            <div style={{ 
              padding: '12px 16px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              margin: '8px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span>Typing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </MessageContainer>
        
        <InputContainer>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isLoading}
          />
          <button
            onClick={() => sendMessage()}
            disabled={isLoading || !inputMessage.trim()}
          >
            Send
          </button>
        </InputContainer>
      </ChatContainer>
    </SalesAgentWrapper>
  );
};

export default SalesAgent;
